/**
 * Locations pane — the stores of one retailer node (and its sub-retailers), or
 * the Unassigned bucket. Tick rows, then Move to… / Unassign in bulk; Add
 * locations opens the trusscore.com finder. Click a row for its details.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Loader2, Plus, CheckSquare, Square, MapPin, Mail, RefreshCw, ExternalLink, Trash2, X, AlertTriangle, ArrowRight } from 'lucide-react';
import { BulkBar } from '../../../components/admin/BulkBar';
import { ConfirmModal } from '../../../components/admin/ConfirmModal';
import { LocationFinderModal } from './LocationFinderModal';
import {
  fetchPartnerLocations, assignPartnerLocations, patchPartnerLocation, deletePartnerLocation,
  type PartnerLocation, type AssignResult,
} from '../../../services/adminPartnersApi';
import type { RetailerTreeNode } from '../../../services/adminRetailerTreeApi';

interface Props {
  /** The selected node; null = the Unassigned bucket. */
  node: RetailerTreeNode | null;
  /** Every node, for the Move to… picker and the "Assigned to" column. */
  nodes: RetailerTreeNode[];
  canEdit: boolean;
  /** Fired after any change so the tree counts refresh. */
  onChanged: () => void;
}

const fmt = (s: string | null | undefined) => (s ? new Date(s.replace(' ', 'T')).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

export const LocationsPane: React.FC<Props> = ({ node, nodes, canEdit, onChanged }) => {
  const [rows, setRows] = useState<PartnerLocation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [ticked, setTicked] = useState<Set<number>>(new Set());
  const [busy, setBusy] = useState(false);
  const [finder, setFinder] = useState(false);
  const [detail, setDetail] = useState<PartnerLocation | null>(null);
  const [move, setMove] = useState<{ ids: number[]; to: number | null } | null>(null);
  const [keepAccess, setKeepAccess] = useState(true);
  const [moveTarget, setMoveTarget] = useState<string>('');

  const nodeById = useMemo(() => new Map(nodes.map(n => [n.id, n])), [nodes]);
  const family = useMemo(() => node ? [node, ...nodes.filter(n => n.parentRetailerId === node.id)] : [], [node, nodes]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      let all: PartnerLocation[] = [];
      if (node === null) {
        const r = await fetchPartnerLocations({ kind: 'retailer', linked: '0', perPage: 200 });
        all = r.locations;
      } else {
        const parts = await Promise.all(family.map(n => fetchPartnerLocations({ kind: 'retailer', retailerId: n.id, perPage: 200 })));
        all = parts.flatMap(p => p.locations);
      }
      setRows(all);
      setTicked(prev => new Set(Array.from(prev).filter(id => all.some(l => l.id === id))));
      if (detail) setDetail(all.find(l => l.id === detail.id) ?? null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not load locations');
    } finally {
      setLoading(false);
    }
  }, [node?.id, family.map(n => n.id).join(',')]);
  useEffect(() => { void load(); }, [load]);

  const shown = useMemo(() => {
    const f = filter.trim().toLowerCase();
    const base = f ? rows.filter(l => [l.name, l.city, l.region, l.email, l.emailOverride, l.sfid, l.street].some(v => (v ?? '').toLowerCase().includes(f))) : rows;
    // Own stores first, then each sub-retailer's, then by name.
    const order = new Map(family.map((n, i) => [n.id, i]));
    return [...base].sort((a, b) => (order.get(a.retailerId ?? -1) ?? 99) - (order.get(b.retailerId ?? -1) ?? 99) || a.name.localeCompare(b.name) || (a.city ?? '').localeCompare(b.city ?? ''));
  }, [rows, filter, family]);

  const allShownTicked = shown.length > 0 && shown.every(l => ticked.has(l.id));
  const toggleAll = () => setTicked(prev => { const n = new Set(prev); if (allShownTicked) shown.forEach(l => n.delete(l.id)); else shown.forEach(l => n.add(l.id)); return n; });
  const toggleOne = (id: number) => setTicked(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });

  const assignedTo = useMemo(() => new Map(rows.map(l => [l.id, l.retailerId ? (nodeById.get(l.retailerId)?.name ?? l.retailerName) : null])), [rows, nodeById]);

  const describe = (r: AssignResult, to: RetailerTreeNode | null) => {
    const kept = r.updated - (r.deleted ?? 0);
    const parts = to
      ? [`${r.updated} location${r.updated === 1 ? '' : 's'} moved to ${to.name}.`]
      : [`${r.deleted ? `${r.deleted} removed from the list (never shared with)` : ''}${r.deleted && kept ? '; ' : ''}${kept ? `${kept} kept under Unassigned (they have shares or estimates)` : ''}.`];
    if (r.movedAccess) parts.push(`${r.movedAccess} person-location access${r.movedAccess === 1 ? '' : 'es'} carried along.`);
    if (r.lostAccess.length) parts.push(`${r.lostAccess.length} ${r.lostAccess.length === 1 ? 'person' : 'people'} lost access through these stores: ${r.lostAccess.map(p => p.email ?? `#${p.userId}`).join(', ')}.`);
    return parts.join(' ');
  };

  const runMove = async () => {
    if (!move) return;
    setBusy(true); setError(null); setMessage(null);
    try {
      const to = move.to !== null ? nodeById.get(move.to) ?? null : null;
      const r = await assignPartnerLocations(move.ids, move.to, keepAccess);
      setMessage(describe(r, to));
      setMove(null); setTicked(new Set());
      await load(); onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Move failed');
    } finally {
      setBusy(false);
    }
  };

  const openMove = (ids: number[], to: number | null) => { setKeepAccess(true); setMove({ ids, to }); };

  const patch = async (id: number, p: Parameters<typeof patchPartnerLocation>[1], ok: string) => {
    setBusy(true); setError(null); setMessage(null);
    try {
      const r = await patchPartnerLocation(id, p);
      setDetail(r.location);
      setMessage(ok);
      await load(); onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Update failed');
    } finally {
      setBusy(false);
    }
  };

  const [del, setDel] = useState<PartnerLocation | null>(null);
  const runDelete = async () => {
    if (!del) return;
    setBusy(true); setError(null); setMessage(null);
    try {
      const r = await deletePartnerLocation(del.id, del.shareCount > 0);
      setMessage(r.purgedShares ? `${del.name} and ${r.purgedShares} share(s) deleted.` : `${del.name} deleted.`);
      setDel(null); setDetail(null);
      await load(); onChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setBusy(false);
    }
  };

  const moveOptions = useMemo(() => {
    const tree = nodes.filter(n => n.id !== 1);
    const roots = tree.filter(n => n.parentRetailerId === null || !nodeById.has(n.parentRetailerId)).sort((a, b) => a.name.localeCompare(b.name));
    const out: { id: number; label: string }[] = [];
    for (const r of roots) {
      out.push({ id: r.id, label: r.name });
      tree.filter(c => c.parentRetailerId === r.id).sort((a, b) => a.name.localeCompare(b.name)).forEach(c => out.push({ id: c.id, label: `  ↳ ${c.name}` }));
    }
    return out;
  }, [nodes, nodeById]);

  const tickedIds = Array.from(ticked);
  const moveTo = move && move.to !== null ? nodeById.get(move.to) ?? null : null;

  return (
    <div>
      {message && <div className="alert alert-success py-2 small d-flex align-items-start gap-2 mb-2">{message}<button type="button" className="btn-close ms-auto" style={{ fontSize: '0.6em' }} aria-label="Dismiss" onClick={() => setMessage(null)} /></div>}
      {error && <div className="alert alert-danger py-2 small d-flex align-items-center gap-2 mb-2"><AlertTriangle size={14} /> {error}</div>}

      <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
        <div className="input-group input-group-sm" style={{ maxWidth: 340 }}>
          <span className="input-group-text"><Search size={14} /></span>
          <input className="form-control" placeholder="Filter: name, city, email, sfid…" value={filter} onChange={e => setFilter(e.target.value)} />
          {filter && <button type="button" className="btn btn-outline-secondary" onClick={() => setFilter('')}><X size={13} /></button>}
        </div>
        <span className="small text-muted">{shown.length}{shown.length !== rows.length ? ` of ${rows.length}` : ''} location{rows.length === 1 ? '' : 's'}</span>
        {canEdit && shown.length > 0 && (
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={toggleAll}>{allShownTicked ? 'Untick all' : `Tick all ${shown.length} shown`}</button>
        )}
        {canEdit && node && (
          <button type="button" className="btn btn-sm btn-primary ms-auto d-inline-flex align-items-center gap-1" onClick={() => setFinder(true)}><Plus size={14} /> Add locations</button>
        )}
      </div>

      {canEdit && (
        <BulkBar count={ticked.size} noun="ticked" onClear={() => setTicked(new Set())}>
          <div className="input-group input-group-sm" style={{ width: 'auto' }}>
            <label className="input-group-text" htmlFor="move-to">Move to</label>
            <select id="move-to" className="form-select form-select-sm" value={moveTarget} onChange={e => setMoveTarget(e.target.value)} style={{ minWidth: 220 }}>
              <option value="">— choose a retailer —</option>
              {moveOptions.map(o => <option key={o.id} value={o.id} disabled={node?.id === o.id}>{o.label}</option>)}
            </select>
            <button type="button" className="btn btn-primary d-inline-flex align-items-center gap-1" disabled={!moveTarget || busy} onClick={() => openMove(tickedIds, Number(moveTarget))}><ArrowRight size={13} /> Move</button>
          </div>
          <button type="button" className="btn btn-sm btn-outline-danger" disabled={busy} onClick={() => openMove(tickedIds, null)}>{node ? 'Unassign' : 'Remove from list'}</button>
        </BulkBar>
      )}

      <div className="table-responsive bg-white rounded-3 border">
        <table className="table table-sm table-hover align-middle mb-0 small">
          <thead className="table-light">
            <tr>
              {canEdit && <th style={{ width: 32 }}><button type="button" className="btn btn-link p-0 text-dark" title={allShownTicked ? 'Untick all' : 'Tick all'} onClick={toggleAll}>{allShownTicked ? <CheckSquare size={16} /> : <Square size={16} />}</button></th>}
              <th>Location</th>
              <th>Assigned to</th>
              <th>Email</th>
              <th className="text-center">Shares</th>
              <th className="text-end">Last share</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan={6} className="text-center text-muted py-4"><Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> Loading…</td></tr>
              : shown.length === 0 ? (
                <tr><td colSpan={6} className="text-center text-muted py-4">
                  {rows.length ? 'Nothing matches the filter.' : node ? <>No locations assigned to {node.name} yet. Use <strong>Add locations</strong> to pull them in from the trusscore.com map.</> : 'Every store is assigned. Stores a customer shares with before an admin has placed them appear here.'}
                </td></tr>
              ) : shown.map(l => {
                const on = ticked.has(l.id);
                const owner = l.retailerId ? nodeById.get(l.retailerId) : null;
                return (
                  <tr key={l.id} className={on ? 'table-active' : ''} style={{ opacity: l.isActive ? 1 : 0.6 }}>
                    {canEdit && <td onClick={() => toggleOne(l.id)} style={{ cursor: 'pointer' }}>{on ? <CheckSquare size={16} className="text-primary" /> : <Square size={16} className="text-muted" />}</td>}
                    <td style={{ cursor: 'pointer' }} onClick={() => setDetail(l)}>
                      <div className="fw-semibold d-flex align-items-center gap-1"><MapPin size={13} className="text-secondary" /> {l.name}{!l.isActive && <span className="badge text-bg-secondary ms-1">inactive</span>}</div>
                      <div className="text-muted" style={{ fontSize: '0.85em' }}>{[l.street, l.city, l.regionCode ?? l.region].filter(Boolean).join(', ')}</div>
                    </td>
                    <td>
                      {!l.retailerId ? <span className="badge text-bg-warning">unassigned</span>
                        : owner && node && owner.id === node.id ? <span className="text-muted">this retailer</span>
                        : <span>{owner?.parentRetailerId && node && owner.parentRetailerId === node.id ? '↳ ' : ''}{owner?.name ?? l.retailerName}{l.retailerActive === false && <span className="text-warning"> (paused)</span>}</span>}
                    </td>
                    <td>{l.email || <span className="text-muted">—</span>}{l.emailOverride && <div className="text-danger" style={{ fontSize: '0.85em' }}><Mail size={11} /> → {l.emailOverride}</div>}</td>
                    <td className="text-center">{l.activeShareCount}{l.shareCount > l.activeShareCount && <span className="text-muted"> / {l.shareCount}</span>}</td>
                    <td className="text-end text-muted">{fmt(l.lastSharedAt)}</td>
                  </tr>
                );
              })}
          </tbody>
        </table>
      </div>

      {finder && node && (
        <LocationFinderModal target={node} assignedTo={assignedTo} onClose={() => setFinder(false)}
          onImported={s => { setFinder(false); setMessage(`${s.added} added, ${s.moved} already in the list (now assigned here)${s.failed ? `, ${s.failed} failed` : ''}.`); void load(); onChanged(); }} />
      )}

      <ConfirmModal open={!!move} title={moveTo ? `Move ${move?.ids.length} location${move?.ids.length === 1 ? '' : 's'} to ${moveTo.name}?` : `${node ? 'Unassign' : 'Remove'} ${move?.ids.length} location${move?.ids.length === 1 ? '' : 's'}?`}
        confirmLabel={moveTo ? 'Move' : node ? 'Unassign' : 'Remove'} variant={moveTo ? 'primary' : 'danger'} busy={busy} onCancel={() => setMove(null)} onConfirm={() => void runMove()}>
        {moveTo ? (
          <>
            <p className="mb-2">Estimates shared with these stores will use <strong>{moveTo.name}</strong>&apos;s price book and branding from now on, and its people will see them.</p>
            <div className="form-check">
              <input id="keep-access" type="checkbox" className="form-check-input" checked={keepAccess} onChange={e => setKeepAccess(e.target.checked)} />
              <label htmlFor="keep-access" className="form-check-label">People who have access to these stores keep it under {moveTo.name}</label>
            </div>
          </>
        ) : !node ? (
          <p className="mb-0">Stores that have never been shared with are removed from the list entirely (you can pull them from trusscore.com again any time). Stores with shares or estimates cannot be removed and stay here.</p>
        ) : (
          <p className="mb-0">Stores that have never been shared with are removed from the list entirely (you can pull them from trusscore.com again any time). Stores with shares or estimates stay under Unassigned: those estimates show quantities only from now on, and anyone whose access was only through these stores loses it.</p>
        )}
      </ConfirmModal>

      {detail && (
        <LocationDetailsModal location={detail} nodeName={detail.retailerId ? (nodeById.get(detail.retailerId)?.name ?? detail.retailerName) : null} canEdit={canEdit} busy={busy}
          onClose={() => setDetail(null)} onPatch={(p, ok) => void patch(detail.id, p, ok)} onDelete={() => setDel(detail)}
          onMove={() => { setDetail(null); setMoveTarget(''); setTicked(new Set([detail.id])); }} />
      )}

      <ConfirmModal open={!!del} title={del && del.shareCount > 0 ? `Delete ${del.name} and its ${del.shareCount} share(s)?` : `Delete ${del?.name}?`} confirmLabel="Delete" variant="danger" busy={busy}
        onCancel={() => setDel(null)} onConfirm={() => void runDelete()}>
        {del && del.shareCount > 0
          ? <p className="mb-0">This permanently deletes the location <strong>and</strong> every share sent to it: the private conversations, event log and promotions with them. The customers&apos; original estimates are untouched. Meant for testing. Deactivating is the safe alternative.</p>
          : <p className="mb-0">It has never been shared with, so nothing else is affected.</p>}
      </ConfirmModal>
    </div>
  );
};

const LocationDetailsModal: React.FC<{
  location: PartnerLocation; nodeName: string | null; canEdit: boolean; busy: boolean;
  onPatch: (p: Parameters<typeof patchPartnerLocation>[1], ok: string) => void;
  onMove: () => void; onDelete: () => void; onClose: () => void;
}> = ({ location: l, nodeName, canEdit, busy, onPatch, onMove, onDelete, onClose }) => {
  const [override, setOverride] = useState(l.emailOverride ?? '');
  useEffect(() => { setOverride(l.emailOverride ?? ''); }, [l.emailOverride]);
  const dirty = override.trim() !== (l.emailOverride ?? '');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);
  return (
    <>
      <div className="modal d-block" tabIndex={-1} role="dialog" aria-modal="true">
        <div className="modal-dialog modal-lg modal-dialog-scrollable">
          <div className="modal-content">
            <div className="modal-header">
              <div>
                <h5 className="modal-title d-flex align-items-center gap-2"><MapPin size={16} /> {l.name}{!l.isActive && <span className="badge text-bg-secondary">inactive</span>}</h5>
                <div className="text-muted small">{[l.street, l.city, l.region, l.postalCode].filter(Boolean).join(', ')}</div>
              </div>
              <button type="button" className="btn-close" aria-label="Close" onClick={onClose} />
            </div>
            <div className="modal-body">
              <dl className="row small mb-3">
                <dt className="col-sm-3">Assigned to</dt><dd className="col-sm-9">{nodeName ? <strong>{nodeName}</strong> : <span className="badge text-bg-warning">unassigned</span>}{canEdit && <button type="button" className="btn btn-link btn-sm p-0 ms-2" onClick={onMove}>move…</button>}</dd>
                <dt className="col-sm-3">Store email</dt><dd className="col-sm-9">{l.email || <span className="text-muted">none on file</span>}</dd>
                <dt className="col-sm-3">Phone</dt><dd className="col-sm-9">{l.phone || '—'}</dd>
                <dt className="col-sm-3">Salesforce id</dt><dd className="col-sm-9 font-monospace">{l.sfid}</dd>
                <dt className="col-sm-3">Profile</dt><dd className="col-sm-9">{l.profileUrl ? <a href={l.profileUrl} target="_blank" rel="noopener noreferrer">trusscore.com <ExternalLink size={11} /></a> : '—'}</dd>
                <dt className="col-sm-3">Shares</dt><dd className="col-sm-9">{l.activeShareCount} active / {l.shareCount} total{l.lastSharedAt && ` · last ${fmt(l.lastSharedAt)}`} · <Link to="/admin/shares">tracking</Link></dd>
                <dt className="col-sm-3">Synced</dt><dd className="col-sm-9">{fmt(l.lastSyncedAt)} {canEdit && <button type="button" className="btn btn-link btn-sm p-0 ms-1" disabled={busy} onClick={() => onPatch({ resync: true }, 'Resynced from trusscore.com.')}><RefreshCw size={12} /> resync</button>}</dd>
              </dl>
              <div className="mb-3">
                <label className="form-label small fw-semibold mb-1 d-flex align-items-center gap-1"><Mail size={13} /> Email override <span className="badge text-bg-danger">testing only</span></label>
                <div className="input-group input-group-sm">
                  <input type="email" className="form-control" placeholder="leave blank in production" value={override} disabled={!canEdit || busy} onChange={e => setOverride(e.target.value)} />
                  {canEdit && <button type="button" className="btn btn-primary" disabled={busy || !dirty} onClick={() => onPatch({ emailOverride: override.trim() }, override.trim() ? 'Override set — sends to this location go to it via SMTP.' : 'Override cleared.')}>Save</button>}
                  {canEdit && l.emailOverride && <button type="button" className="btn btn-outline-secondary" disabled={busy} onClick={() => { setOverride(''); onPatch({ emailOverride: '' }, 'Override cleared.'); }}><X size={13} /></button>}
                </div>
                <div className="form-text">While set, every share to this location is delivered here via this server&apos;s SMTP (Pardot is not contacted), even when the global redirect is on.</div>
              </div>
              {canEdit && (
                <div className="d-flex align-items-center gap-3">
                  <div className="form-check form-switch mb-0">
                    <input id="loc-active" type="checkbox" className="form-check-input" role="switch" checked={l.isActive} disabled={busy} onChange={e => onPatch({ isActive: e.target.checked }, e.target.checked ? 'Location activated.' : 'Location deactivated — new shares to it are refused.')} />
                    <label htmlFor="loc-active" className="form-check-label small">Active</label>
                  </div>
                  <button type="button" className="btn btn-sm btn-outline-danger ms-auto d-inline-flex align-items-center gap-1" disabled={busy} onClick={onDelete}><Trash2 size={13} /> Delete{l.shareCount > 0 ? ' (purge)' : ''}</button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop show" />
    </>
  );
};

export default LocationsPane;
